import type { ComponentProps } from 'react'

import { cn } from '@morpho-org/ui-v6/utils'
import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Skeleton variants using cva for Tailwind class management.
 *
 * Matches v5 Skeleton behavior:
 * - States: rectangle (rounded-[20px] = 20px, v5 default), circle (rounded-full)
 * - Animation: wave effect using linear gradient
 */
const skeletonVariants = cva(['bg-muted relative overflow-hidden'], {
  variants: {
    variant: {
      rectangle: 'rounded-[20px]',
      circle: 'rounded-full'
    }
  },
  defaultVariants: {
    variant: 'rectangle'
  }
})

export interface SkeletonProps
  extends Omit<ComponentProps<'div'>, 'children'>, VariantProps<typeof skeletonVariants> {}

/**
 * Skeleton — a Tailwind replica of the dapp-ui-v5 Skeleton. Size, margin and
 * radius are set with native Tailwind classes via `className` (or `style` for
 * runtime-computed dimensions).
 *
 * @example
 * ```tsx
 * <Skeleton className="h-[20px] w-full" />
 * <Skeleton variant="circle" className="size-[40px]" />
 * ```
 */
function Skeleton({ className, variant, ...props }: SkeletonProps) {
  return (
    <div className={cn(skeletonVariants({ variant }), className)} {...props}>
      <div className="animate-skeleton-wave via-muted absolute inset-0 bg-linear-to-r from-transparent to-transparent" />
    </div>
  )
}

export { Skeleton, skeletonVariants }
